import React, { useEffect, useMemo, useState } from "react";
import Insufficiency from "./hr-pending-action/Insufficiency";
import ExtraCost from "./hr-pending-action/ExtraCost";
import Footer from "../../../core/common/Footer";
import StoppedCases from "./StoppedCases";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import Reports from "./Reports";
import { PageLoader } from "../../../core/common/loader/PageLoader";
import {
  ExclamationCircleOutlined,
  DollarCircleOutlined,
  StopOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

type HrTabLabel =
  | "Insufficiency Checks"
  | "Additional Cost Approval"
  | "Stopped Checks"
  | "All Reports";

const componentMap: Record<HrTabLabel, React.ComponentType<any>> = {
  "Insufficiency Checks": Insufficiency,
  "Additional Cost Approval": ExtraCost,
  "Stopped Checks": StoppedCases,
  "All Reports": Reports,
};

const iconMap: Record<HrTabLabel, React.ReactNode> = {
  "Insufficiency Checks": <ExclamationCircleOutlined />,
  "Additional Cost Approval": <DollarCircleOutlined />,
  "Stopped Checks": <StopOutlined />,
  "All Reports": <FileTextOutlined />,
};

export const statusColors: Record<string, string> = {
  Pending: "bg-warning",
  Completed: "bg-soft-success",
  "In Progress": "bg-soft-secondary",
};

const ACTIVE_TAB_KEY = "active-hr-action-tab";

const HrPendingAction = () => {
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_KEY) || "";
    } catch {
      return "";
    }
  });

  const { data: fetchHrActionCount, loading } = useApi(
    webservice_url.hr_action_count,
    {
      method: "GET",
      autoCall: true,
    },
  );

  const dynamicTabs = useMemo(
    () =>
      (
        (fetchHrActionCount?.data as
          | { status_id: string; label: HrTabLabel; count: number }[]
          | undefined) ?? []
      ).map((item) => ({
        id: item?.label,
        status_id: item?.status_id ?? "",
        label: item?.label,
        count: item?.count,
        Component: componentMap[item?.label],
      })),
    [fetchHrActionCount],
  );

  useEffect(() => {
    if (dynamicTabs.length === 0) return;
    const exists = dynamicTabs.some((tab) => tab.label === activeTab);

    if (!exists) {
      setActiveTab(dynamicTabs[0].label);
    }
  }, [dynamicTabs, activeTab]);

  const activeTabData = dynamicTabs.find((tab) => tab.label === activeTab);

  const handleTabClick = (tab: { label: string }) => {
    setActiveTab(tab.label);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, tab.label);
    } catch (err) {
      console.error("Failed to save active tab to localStorage:", err);
    }
  };
  const CaseMark = () => (
    <svg
      className="tcp-case-mark"
      viewBox="0 0 40 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="14" width="4" height="18" rx="1.5" />
      <rect x="9" y="8" width="4" height="24" rx="1.5" />
      <rect x="18" y="0" width="4" height="32" rx="1.5" />
      <rect x="27" y="10" width="4" height="22" rx="1.5" />
      <rect x="36" y="18" width="4" height="14" rx="1.5" />
    </svg>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        {loading ? (
          <PageLoader />
        ) : (
          <>
            <div className="tcp-header-title-row d-flex gap-3 mb-3">
              <CaseMark />
              <div>
                <h4 className="tcp-title">HR Action Required</h4>
                <p className="tcp-subtitle">
                  Review screening checks requiring HR attention, resolve
                  insufficiencies, and take action on stopped checks.
                </p>
              </div>
            </div>
            <ul className="invite-tabs">
              {dynamicTabs.map((tab) => (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`invite-btn ${
                      activeTab === tab.label ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span className="d-flex align-items-center justify-content-center gap-2">
                      {iconMap[tab.label]}
                      <span>{tab.label}</span>
                    </span>

                    {typeof tab.count === "number" && (
                      <span className="d-block fw-semibold">{tab.count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {/* Tab Content */}
            <div className="mt-3">
              {activeTabData?.Component && (
                <activeTabData.Component status_id={activeTabData?.status_id} />
              )}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default HrPendingAction;
